import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { fetchAllPages } from "../../utils/exportHelpers";
import { Box, Paper, Typography, Switch, Menu, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CalendarDate } from "@internationalized/date";
import { useDispatch, useSelector } from "react-redux";
import InfoTooltip from "../../common_components/InfoTooltip";
import { triggerRefresh, checkRefreshStatus, selectIsSyncing } from "../../redux/refreshSlice";
import { fetchAffiliateProjection, setMonth, setAccountManagerId, setClientType, setTotalGoalToggle, setPage, setSortConfig, selectAffiliateProjectionLastUpdatedAt, selectAffiliateProjectionOverallStatus, selectAffiliateProjectionFilters, selectAffiliateProjectionRefreshLoading, } from "../../redux/affiliateprojectionslice";
import { fetchDesignationWiseUsers, selectDesignationWiseUsers } from "../../redux/clientDataSlice";
import { AM_DESIGNATIONS, collectUsersByDesignations } from "../../utils/designationFilters";
import { fetchVisibleClients, hideClients, selectVisibleClients, unhideClient } from "../../redux/accountSummarySlice";
import { setClientContext } from "../../redux/accountOverviewSlice";
import { useAccountOverview, ACCOUNT_OVERVIEW_SESSION_KEY } from "../../context/AccountOverviewContext";
import { ROUTES } from "../../routes/routes.constants";
// Components
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import { Dropdown, ExportButton, MonthPicker } from "../../common_components/FormInputs";
import CommonTable from "../../common_components/CommonTable";
import Pagination from "../../common_components/Pagination";
import CommonChip from "../../common_components/CommonChip";
import CommonLoader from "../../common_components/CommonLoader";
import VisibleClientsDropdown from "../../common_components/VisibleClientsDropdown";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import { useOverlayHoverHandlers } from "../../common_components/hoverOverlay/HoverOverlayProvider";
import { useLayout } from "../../contexts/LayoutContext";
import { SUMMARY_ROW_POSITIONS, TABLE_TYPES, EXPAND_TRIGGERS } from "../../common_components/tableConstants";
import { truncateName } from "../../helper/truncateName";
import exportAffiliateProjection from "./Exportaffiliateprojection";
import { exportPublisherArticles } from "./exportPublisherArticles";
import { ApiCall } from "../../helper/axios";
// Extracted Components
import { VarianceBadge } from "./components/VarianceBadge";
import { CurrencyText } from "./components/CurrencyText";
import { McIcon } from "./components/McIcon";
import { ToggleTooltipContent } from "./components/ToggleTooltipContent";
import { ExportArticlesDialog } from "./components/ExportArticlesDialog";
// Utils
import { NETWORK_ICON_MAP, STATUS_MAP, tableContainerSx } from "./utils/constants";
import { getDefaultMonth, splitAmNames, getInitials } from "./utils/helpers";
import moment from "moment";
const getColumns = (hasBaselineAccess) => {
    const baseColumns = [
        { id: "client", label: "Client", width: "196px", align: "left", sortable: true },
        { id: "am", label: "AM", width: "80px", align: "center", sortable: false },
        {
            id: "platformGroup", label: "Platform", align: "center", children: [
                { id: "platformMc", label: "MC", width: "80px", align: "center", sortable: false },
                { id: "platformGoal", label: "Goal", width: "120px", align: "center", sortable: true },
                { id: "platformMtd", label: "MTD", width: "120px", align: "center", sortable: true },
                { id: "platformVariance", label: "VAR", width: "120px", align: "center", sortable: true },
            ]
        },
        {
            id: "affiliateGroup", label: "Affiliate", align: "center", children: [
                { id: "affiliateMc", label: "MC", width: "80px", align: "center", sortable: false },
                { id: "affiliateGoal", label: "Goal", width: "120px", align: "center", sortable: true },
                { id: "affiliateMtd", label: "MTD", width: "120px", align: "center", sortable: true },
                { id: "affiliateVariance", label: "VAR", width: "120px", align: "center", sortable: true },
            ]
        }
    ];
    if (hasBaselineAccess) {
        baseColumns.push({
            id: "commissionGroup", label: "Commission", align: "center", children: [
                { id: "commissionBaseline", label: "Baseline", width: "120px", align: "right", sortable: true },
            ]
        });
    }
    return baseColumns;
};
export default function AffiliateAccountPerformance() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const overlayHover = useOverlayHoverHandlers();
    const { isSidebarExpanded, setIsSidebarExpanded } = useLayout();
    const initialized = useRef(false);
    // ─── Selectors ────────────────────────────────────────────────────────────
    const { clients, totalSummary, pagination, loading, month, accountManagerId, clientType, totalGoalToggle, page, limit, sortBy, sortOrder, } = useSelector((state) => state.affiliateProjection);
    const overallStatus = useSelector(selectAffiliateProjectionOverallStatus);
    const refreshLoading = useSelector(selectAffiliateProjectionRefreshLoading);
    const isSyncing = useSelector(selectIsSyncing);
    const lastUpdatedAt = useSelector(selectAffiliateProjectionLastUpdatedAt);
    const filters = useSelector(selectAffiliateProjectionFilters);
    const designationWiseUsers = useSelector(selectDesignationWiseUsers);
    const visibleClients = useSelector(selectVisibleClients);
    const permissions = useSelector((state) => state.auth.permissions);
    const token = useSelector((state) => state.auth.token);
    const isGrantScope = permissions?.affiliate_account_performance?.client_scope === "all";
    const affiliateActions = permissions?.affiliate_account_performance?.actions || [];
    const hasBaselineAccess = affiliateActions.includes("Baseline");
    const hasExportArticlesAccess = affiliateActions.includes("Export Articles");
    // ─── Local state ──────────────────────────────────────────────────────────
    const [visibleClientsList, setVisibleClientsList] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [toggleLoading, setToggleLoading] = useState(false);
    const [exportingArticles, setExportingArticles] = useState(false);
    const [exportArticlesModalOpen, setExportArticlesModalOpen] = useState(false);
    const [domainUrl, setDomainUrl] = useState("");
    const [exportArticlesError, setExportArticlesError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);
    // ─── Memoized ─────────────────────────────────────────────────────────────
    const statusText = useMemo(() => STATUS_MAP[overallStatus || ""] || "Checking...", [overallStatus]);
    const accountManagers = useMemo(() => collectUsersByDesignations(designationWiseUsers?.designationMap, AM_DESIGNATIONS), [designationWiseUsers]);
    const amOptions = useMemo(() => [
        { value: "", label: "All" },
        ...accountManagers.map((u) => ({ value: u._id, label: `${u.first_name} ${u.last_name}` })),
    ], [accountManagers]);
    const selectedAm = useMemo(() => accountManagers.find((u) => u._id === accountManagerId), [accountManagerId, accountManagers]);
    const clientTypeOptions = useMemo(() => {
        const base = ["View All", "Custom Client", "Revenue", "Orders"];
        const extra = (filters || []).filter(f => !base.includes(f));
        return [
            { value: "View All", label: "View All" },
            { value: "Revenue", label: "Revenue" },
            { value: "Orders", label: "Orders" },
            ...extra.map(f => ({ value: f, label: f })),
            { value: "Custom Client", label: "Custom Client" },
        ];
    }, [filters]);
    const selectedCalendarDate = useMemo(() => {
        if (!month)
            return null;
        const [y, m] = month.split("-").map(Number);
        return new CalendarDate(y, m, 1);
    }, [month]);
    // ─── Init ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (initialized.current)
            return;
        initialized.current = true;
        const currentMonth = month || getDefaultMonth();
        if (!month)
            dispatch(setMonth(currentMonth));
        Promise.all([
            dispatch(fetchDesignationWiseUsers()),
            dispatch(fetchVisibleClients({ moduleKey: "affiliate_account_performance", month: currentMonth })),
        ]);
    }, []);
    useEffect(() => {
        if (clientType === "Custom Client") {
            setIsSidebarExpanded(false);
        }
    }, [clientType, setIsSidebarExpanded]);
    useEffect(() => {
        if (visibleClients?.length) {
            setVisibleClientsList(visibleClients.map((c) => ({ id: c._id, name: c.name, visible: c.visible })));
        }
    }, [visibleClients]);
    const doFetch = useCallback(() => {
        if (!month) {
            return;
        }
        dispatch(fetchVisibleClients({ moduleKey: "affiliate_account_performance", month }));
        return dispatch(fetchAffiliateProjection({
            month,
            account_manager_id: accountManagerId || undefined,
            client_type: clientType || "View All",
            sortBy, sortOrder, page, limit,
        }));
    }, [dispatch, month, accountManagerId, clientType, page, limit, sortBy, sortOrder]);
    useEffect(() => {
        if (!month)
            return;
        doFetch();
    }, [month, accountManagerId, clientType, page, limit, sortBy, sortOrder, doFetch]);
    // ─── Sync Polling ─────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(checkRefreshStatus({ moduleKey: "affiliate_account_performance" }));
    }, [dispatch]);
    useEffect(() => {
        let interval;
        if (isSyncing) {
            interval = setInterval(() => {
                dispatch(checkRefreshStatus({ moduleKey: "affiliate_account_performance" }));
            }, 30000); // Poll every 30 seconds
        }
        return () => {
            if (interval)
                clearInterval(interval);
        };
    }, [isSyncing, dispatch]);
    const prevIsSyncing = useRef(isSyncing);
    useEffect(() => {
        if (prevIsSyncing.current && !isSyncing) {
            doFetch();
        }
        prevIsSyncing.current = isSyncing;
    }, [isSyncing, doFetch]);
    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handleRefresh = useCallback(async () => {
        if (!month || refreshLoading || isSyncing)
            return;
        const clientIds = visibleClientsList.map((c) => c.id).filter(Boolean);
        dispatch(triggerRefresh({
            moduleKey: "affiliate_account_performance",
            clientIds,
            dateRange: [{ startDate: `${month}-01`, endDate: `${month}-31` }]
        }));
    }, [dispatch, month, refreshLoading, visibleClientsList, isSyncing]);
    const handleClientToggle = useCallback(async (clientId, visible) => {
        setVisibleClientsList(prev => prev.map(c => c.id === clientId ? { ...c, visible } : c));
        setToggleLoading(true);
        try {
            visible
                ? await dispatch(unhideClient({ clientId, moduleKey: "affiliate_account_performance" })).unwrap()
                : await dispatch(hideClients({ client_ids: [clientId], reason: null, moduleKey: "affiliate_account_performance" })).unwrap();
            await dispatch(fetchVisibleClients({ moduleKey: "affiliate_account_performance", month })).unwrap();
            await doFetch();
        }
        catch (e) {
            setVisibleClientsList(prev => prev.map(c => c.id === clientId ? { ...c, visible: !visible } : c));
        }
        finally {
            setToggleLoading(false);
        }
    }, [dispatch, doFetch]);
    const handleBulkToggle = useCallback(async (ids, visible) => {
        const idSet = new Set(ids);
        setVisibleClientsList(prev => prev.map(c => idSet.has(c.id) ? { ...c, visible } : c));
        setToggleLoading(true);
        try {
            visible
                ? await dispatch(unhideClient({ client_ids: ids, moduleKey: "affiliate_account_performance" })).unwrap()
                : await dispatch(hideClients({ client_ids: ids, reason: null, moduleKey: "affiliate_account_performance" })).unwrap();
            await dispatch(fetchVisibleClients({ moduleKey: "affiliate_account_performance", month })).unwrap();
            await doFetch();
        }
        catch (e) {
            setVisibleClientsList(prev => prev.map(c => idSet.has(c.id) ? { ...c, visible: !visible } : c));
        }
        finally {
            setToggleLoading(false);
        }
    }, [dispatch, doFetch]);
    const handleExport = useCallback(async () => {
        if (!month || exporting)
            return;
        setExporting(true);
        try {
            // Fetch all pages in safe batches instead of one massive request
            let cachedSummary = null;
            const allClients = await fetchAllPages(async (page, limit) => {
                const result = await dispatch(fetchAffiliateProjection({
                    month,
                    total_goal_toggle: totalGoalToggle,
                    account_manager_id: accountManagerId || undefined,
                    client_type: clientType || "View All",
                    page,
                    limit,
                })).unwrap();
                if (!cachedSummary)
                    cachedSummary = result.total_summary;
                return {
                    items: result.clients || [],
                    totalPages: result.pagination?.total_pages || 1,
                };
            });
            exportAffiliateProjection(allClients, cachedSummary, month, clientType);
        }
        catch (error) {
            console.error("Export failed:", error);
        }
        finally {
            setExporting(false);
        }
    }, [dispatch, month, totalGoalToggle, accountManagerId, clientType, exporting]);
    const handleExportArticlesSubmit = useCallback(async () => {
        if (exportingArticles || !domainUrl.trim())
            return;
        setExportingArticles(true);
        setExportArticlesError(null);
        try {
            let extractedDomain = domainUrl.trim();
            try {
                if (extractedDomain.startsWith('http')) {
                    extractedDomain = new URL(extractedDomain).hostname.replace('www.', '');
                }
                else if (extractedDomain.includes('/')) {
                    extractedDomain = new URL('https://' + extractedDomain).hostname.replace('www.', '');
                }
                else {
                    extractedDomain = extractedDomain.replace('www.', '');
                }
            }
            catch (e) { }
            const res = await ApiCall('GET', `/api/affiliate-tracker/export-articles?domain=${encodeURIComponent(extractedDomain)}`, null, { Authorization: token });
            const data = res?.data;
            if (data?.success) {
                const exported = exportPublisherArticles(data.data || [], extractedDomain);
                if (exported) {
                    setExportArticlesModalOpen(false);
                    setDomainUrl("");
                }
                else {
                    setExportArticlesError('No data found for this domain.');
                }
            }
            else {
                setExportArticlesError(data?.message || 'No records found for this domain.');
            }
        }
        catch {
            setExportArticlesError('Failed to export articles. Please try again.');
        }
        finally {
            setExportingArticles(false);
        }
    }, [exportingArticles, domainUrl, token]);
    const handleMenuClick = useCallback((event) => {
        if (hasExportArticlesAccess) {
            setAnchorEl(event.currentTarget);
        }
        else {
            handleExport();
        }
    }, [hasExportArticlesAccess, handleExport]);
    const handleMenuClose = useCallback(() => {
        setAnchorEl(null);
    }, []);
    const handleExportDataClick = useCallback(() => {
        handleMenuClose();
        handleExport();
    }, [handleMenuClose, handleExport]);
    const handleExportArticlesClick = useCallback(() => {
        handleMenuClose();
        setExportArticlesError(null);
        setExportArticlesModalOpen(true);
    }, [handleMenuClose]);
    const renderDropdownValue = useCallback((label, value) => (_jsxs("span", { className: "all-Font-Sizes", children: [_jsxs("span", { className: "all-Font-Sizes", style: { color: "#101828", fontWeight: 500 }, children: [label, ":"] }), " ", _jsx("span", { className: "all-Font-Sizes", style: { color: "#667085" }, children: value })] })), []);
    const { setContextState } = useAccountOverview();
    // ─── Render cell ──────────────────────────────────────────────────────────
    const renderCell = useCallback((row, col) => {
        // "Orders" account type reports affiliate Goal/MTD as counts, not revenue — no "$" prefix.
        const isOrdersClient = row.account_type?.toLowerCase() === "orders";
        switch (col.id) {
            case "client":
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, px: 0.1, overflow: "hidden" }, children: [_jsx(HoverTooltip, { title: row.name, placement: "top", arrow: true, children: _jsx(Typography, { className: "all-Font-Sizes", sx: {
                                    fontSize: "14px !important",
                                    fontWeight: 400, color: "#27272a", whiteSpace: "nowrap",
                                    overflow: "hidden", textOverflow: "ellipsis", maxWidth: "145px",
                                    cursor: "pointer", display: "block"
                                }, onClick: () => {
                                    const m = moment(month, 'YYYY-MM');
                                    const startDate = m.clone().startOf('month').format('YYYY-MM-DD');
                                    const today = moment();
                                    const endMoment = m.isSame(today, 'month')
                                        ? today.clone().subtract(1, 'day')
                                        : m.clone().endOf('month');
                                    const endDate = endMoment.format('YYYY-MM-DD');
                                    const prevMonth = m.clone().subtract(1, 'month');
                                    const navState = {
                                        selectedClientId: row.id,
                                        selectedMonth: month,
                                        dateRange: { start: startDate, end: endDate },
                                        compareDateRange: {
                                            start: prevMonth.clone().startOf('month').format('YYYY-MM-DD'),
                                            end: endMoment.clone().subtract(1, 'month').format('YYYY-MM-DD'),
                                        },
                                        clientType: clientType,
                                        totalGoalToggle: totalGoalToggle,
                                        activeTab: 'account_overview'
                                    };
                                    // Write synchronously so useDateRange reads it before React commits
                                    const saved = JSON.parse(sessionStorage.getItem(ACCOUNT_OVERVIEW_SESSION_KEY) || '{}');
                                    sessionStorage.setItem(ACCOUNT_OVERVIEW_SESSION_KEY, JSON.stringify({ ...saved, ...navState }));
                                    dispatch(setClientContext({ clientId: row.id, clientName: row.name }));
                                    setContextState(navState);
                                    // Update global selected client
                                    localStorage.setItem('globalSelectedClientId', row.id);
                                    localStorage.setItem('globalSelectedGroupAccountId', '');
                                    window.dispatchEvent(new Event('globalClientChange'));
                                    navigate(ROUTES.accountOverview.path);
                                }, children: row.name }) }), _jsx("img", { src: "/assets/link.svg", alt: "link", style: { flexShrink: 0, cursor: "pointer" }, onClick: () => {
                                const m = moment(month, 'YYYY-MM');
                                const startDate = m.clone().startOf('month').format('YYYY-MM-DD');
                                const today = moment();
                                const endMoment = m.isSame(today, 'month')
                                    ? today.clone().subtract(1, 'day')
                                    : m.clone().endOf('month');
                                const endDate = endMoment.format('YYYY-MM-DD');
                                const prevMonth = m.clone().subtract(1, 'month');
                                const navState = {
                                    selectedClientId: row.id,
                                    selectedMonth: month,
                                    dateRange: { start: startDate, end: endDate },
                                    compareDateRange: {
                                        start: prevMonth.clone().startOf('month').format('YYYY-MM-DD'),
                                        end: endMoment.clone().subtract(1, 'month').format('YYYY-MM-DD'),
                                    },
                                    clientType: clientType,
                                    totalGoalToggle: totalGoalToggle,
                                    activeTab: 'account_overview'
                                };
                                // Write synchronously so useDateRange reads it before React commits
                                const saved = JSON.parse(sessionStorage.getItem(ACCOUNT_OVERVIEW_SESSION_KEY) || '{}');
                                sessionStorage.setItem(ACCOUNT_OVERVIEW_SESSION_KEY, JSON.stringify({ ...saved, ...navState }));
                                dispatch(setClientContext({ clientId: row.id, clientName: row.name }));
                                setContextState(navState);
                                // Update global selected client
                                localStorage.setItem('globalSelectedClientId', row.id);
                                localStorage.setItem('globalSelectedGroupAccountId', '');
                                window.dispatchEvent(new Event('globalClientChange'));
                                navigate(ROUTES.accountOverview.path);
                            } })] }));
            case "am": {
                const names = splitAmNames(row.am);
                const visible = names.slice(0, 2);
                const extra = names.slice(2);
                return (_jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsxs(Box, { sx: { display: "flex", position: "relative", zIndex: 1 }, children: [visible.map((name, idx) => (_jsx(CommonChip, { label: getInitials(name), tooltip: name, renderType: "circle", index: idx, className: `circle manager-circle ${idx === 0 ? "first" : ""} ${name === "NA" ? "na" : "ok"}`, ...overlayHover }, `${name}-${idx}`))), extra.length > 0 && (_jsx(CommonChip, { label: `+${extra.length}`, renderType: "circle", className: "circle extra-circle manager-circle", tooltip: _jsx(Box, { sx: { background: "black", borderRadius: "8px", overflow: "hidden", minWidth: 180 }, children: extra.map((n, i) => (_jsx(Box, { className: "all-Font-Sizes", sx: { px: 1, py: 1, textTransform: "capitalize", borderBottom: i !== extra.length - 1 ? "1px solid rgba(255,255,255,0.5)" : "none" }, children: n }, `${n}-${i}`))) }), ...overlayHover }))] }) }));
            }
            case "platformMc": return (_jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsx(HoverTooltip, { title: NETWORK_ICON_MAP[row.platform.mc?.toLowerCase() || ""]?.name || row.platform.mc || "No MC", children: _jsx(Box, { sx: { display: "flex" }, children: _jsx(McIcon, { mc: row.platform.mc }) }) }) }));
            case "platformGoal": return _jsx(CurrencyText, { value: row.platform.goal });
            case "platformMtd": return _jsx(CurrencyText, { value: row.platform.mtd });
            case "platformVariance": return _jsx(Box, { sx: { display: "flex", justifyContent: "flex-start" }, children: _jsx(VarianceBadge, { value: row.platform.variance_percent }) });
            case "affiliateMc": {
                const visibleMcs = (row.affiliate.mc || []).filter((mc) => mc?.toLowerCase() !== "shareasale");
                return (_jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsx(Box, { sx: { display: "flex", position: "relative", zIndex: 1 }, children: visibleMcs.length > 0 ? visibleMcs.slice(0, 3).map((mc, i) => (_jsx(HoverTooltip, { title: NETWORK_ICON_MAP[mc.toLowerCase()]?.name || mc, children: _jsx(Box, { sx: { display: "flex", ml: i > 0 ? "-8px" : 0, position: "relative", zIndex: i, borderRadius: "50%", border: "2px solid #fff", bgcolor: "#fff", transition: "all 0.2s ease", "&:hover": { zIndex: 10, transform: "translateY(-2px)" } }, children: _jsx(McIcon, { mc: mc, rounded: true }) }) }, i))) : _jsx(Box, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", color: "#a1a1aa" }, children: "\u2014" }) }) }));
            }
            case "affiliateGoal": return _jsx(CurrencyText, { value: totalGoalToggle ? (row.affiliate.goal_total ?? 0) : row.affiliate.goal, showCurrency: !isOrdersClient });
            case "affiliateMtd": return _jsx(CurrencyText, { value: clientType === "Custom Client" ? (totalGoalToggle ? (row.affiliate.custom_mtd_total ?? 0) : (row.affiliate.custom_mtd ?? 0)) : row.affiliate.mtd, showCurrency: !isOrdersClient });
            case "affiliateVariance": return _jsx(Box, { sx: { display: "flex", justifyContent: "flex-start" }, children: _jsx(VarianceBadge, { value: clientType === "Custom Client" ? (totalGoalToggle ? (row.affiliate.custom_variance_total ?? 0) : (row.affiliate.custom_variance ?? 0)) : (totalGoalToggle ? (row.affiliate.variance_percent_total ?? 0) : row.affiliate.variance_percent) }) });
            case "commissionBaseline": return _jsx(CurrencyText, { value: row.commission.baseline, showCurrency: !isOrdersClient });
            default: return null;
        }
    }, [overlayHover, totalGoalToggle, clientType, month, dispatch, navigate, setContextState]);
    // ─── Summary row ──────────────────────────────────────────────────────────
    const renderSummaryRow = useCallback((_, col) => {
        if (col.id === "client")
            return _jsx(Box, { sx: { px: 0.1 }, children: _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", fontWeight: 700, color: "#181d27" }, children: "Total Summary" }) });
        if (["am", "platformMc", "affiliateMc"].includes(col.id))
            return "";
        if (!totalSummary)
            return "";
        // When viewing the "Orders" type, the affiliate summary aggregates order counts, not revenue — no "$" prefix.
        const isOrdersView = clientType?.toLowerCase() === "orders";
        switch (col.id) {
            case "platformGoal": return _jsx(CurrencyText, { value: totalSummary.platform.goal, bold: true });
            case "platformMtd": return _jsx(CurrencyText, { value: totalSummary.platform.mtd, bold: true });
            case "platformVariance": return _jsx(Box, { sx: { display: "flex", justifyContent: "start", px: 0.1 }, children: _jsx(VarianceBadge, { value: totalSummary.platform.variance_percent }) });
            case "affiliateGoal": return _jsx(CurrencyText, { value: totalGoalToggle ? (totalSummary.affiliate.goal_total ?? 0) : totalSummary.affiliate.goal, bold: true, showCurrency: !isOrdersView });
            case "affiliateMtd": return _jsx(CurrencyText, { value: clientType === "Custom Client" ? (totalGoalToggle ? (totalSummary.affiliate.custom_mtd_total ?? 0) : (totalSummary.affiliate.custom_mtd ?? 0)) : totalSummary.affiliate.mtd, bold: true, showCurrency: !isOrdersView });
            case "affiliateVariance": return _jsx(Box, { sx: { display: "flex", justifyContent: "start", px: 0.1 }, children: _jsx(VarianceBadge, { value: clientType === "Custom Client" ? (totalGoalToggle ? (totalSummary.affiliate.custom_variance_total ?? 0) : (totalSummary.affiliate.custom_variance ?? 0)) : (totalGoalToggle ? (totalSummary.affiliate.variance_percent_total ?? 0) : totalSummary.affiliate.variance_percent) }) });
            // Commission Baseline is a per-client value, not a sum — no footer total.
            case "commissionBaseline": return "";
            default: return "";
        }
    }, [totalSummary, hasBaselineAccess, totalGoalToggle, clientType]);
    const isRefreshDisabled = refreshLoading || isSyncing;
    const showSummaryRow = clients.length > 0;
    return (_jsxs(PageContainer, { children: [_jsxs(Paper, { elevation: 0, sx: { display: "flex", flexDirection: "column", gap: "8px" }, children: [_jsx(PagesCommonHeader, { height: "65px", rightCss: (clientType === "Custom Client" && isSidebarExpanded) ? "affiliate-header-width" : "", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: lastUpdatedAt, statusText: statusText, onRefresh: handleRefresh, loading: refreshLoading, moduleName: "affiliate_account_performance", disabled: isRefreshDisabled, disabledReason: isSyncing ? "Data is being updated in the background. Please revisit/refresh this page after 10 mins" : "" }), rightSection: _jsxs(Box, { display: "flex", gap: 1, alignItems: "center", sx: { flexWrap: (clientType === "Custom Client" && isSidebarExpanded) ? "wrap" : "nowrap", justifyContent: (clientType === "Custom Client" && isSidebarExpanded) ? "flex-end" : "flex-start", width: "100%" }, children: [isGrantScope && _jsx(Dropdown, { value: clientType, onChange: (v) => dispatch(setClientType(v)), options: clientTypeOptions, displayValue: renderDropdownValue("Client Type", truncateName(clientTypeOptions.find(t => t.value === clientType)?.label || "View All", 10)), leftIcon: "/assets/TypeC.svg", size: "sm", fullWidth: false, className: "header-dropdown", sortByLabel: false }), clientType === "Custom Client" && clients.length > 0 && (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.2, border: "1px solid #ddd", padding: "0px 12px", borderRadius: "8px", bgcolor: "#fff", height: "42px", transition: "all 0.2s", "&:hover": { borderColor: "#ff008e" } }, children: [_jsx("img", { src: "/assets/target.svg", alt: "" }), _jsx(Box, { className: "compare-toggle-container", sx: { display: "flex", alignItems: "center" }, children: _jsx(Switch, { checked: totalGoalToggle, onChange: (e) => dispatch(setTotalGoalToggle(e.target.checked)), sx: { width: 44, height: 24, padding: 0, display: "flex", "&:active": { "& .MuiSwitch-thumb": { width: 20 }, "& .MuiSwitch-switchBase.Mui-checked": { transform: "translateX(16px)" } }, "& .MuiSwitch-switchBase": { padding: "2px", "&.Mui-checked": { transform: "translateX(20px)", color: "#fff", "& + .MuiSwitch-track": { opacity: 1, backgroundColor: "#ff008e", "&::before": { content: "''", position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", width: "2px", height: "10px", backgroundColor: "#fff", borderRadius: "1px" } } } }, "& .MuiSwitch-thumb": { width: 20, height: 20, borderRadius: 10, boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)" }, "& .MuiSwitch-track": { borderRadius: 24 / 2, opacity: 1, backgroundColor: "#E9E9EA", boxSizing: "border-box", position: "relative" } } }) }), _jsx(InfoTooltip, { title: _jsx(ToggleTooltipContent, {}), placement: "bottom" }), _jsx(Typography, { className: "all-Font-Sizes", sx: { fontWeight: 500, color: "#344054", whiteSpace: "nowrap" }, children: "Total Goal" })] })), isGrantScope && _jsx(Dropdown, { value: accountManagerId, onChange: (v) => dispatch(setAccountManagerId(v)), options: amOptions, displayValue: renderDropdownValue("AM", truncateName(selectedAm ? `${selectedAm.first_name} ${selectedAm.last_name}` : "All", 10)), leftIcon: "/assets/userC.svg", size: "sm", fullWidth: false, className: "header-dropdown", searchable: accountManagers.length > 10 }), _jsx(MonthPicker, { showAllTime: false, value: selectedCalendarDate, onChange: (v) => v !== "ALL_TIME" && dispatch(setMonth(`${v.year}-${String(v.month).padStart(2, "0")}`)), fullWidth: false, className: "header-dropdown" }), isGrantScope && visibleClientsList.length > 0 && _jsx(VisibleClientsDropdown, { clients: visibleClientsList, onChange: setVisibleClientsList, label: "Visible Client", onToggle: handleClientToggle, onBulkToggle: handleBulkToggle }), _jsx(ExportButton, { loading: exporting || exportingArticles, onClick: handleMenuClick, label: "Export" }), hasExportArticlesAccess && (_jsxs(Menu, { anchorEl: anchorEl, open: openMenu, onClose: handleMenuClose, MenuListProps: {
                                        'aria-labelledby': 'basic-button',
                                    }, anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }, transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }, sx: {
                                        mt: 0.5,
                                        '& .MuiPaper-root': {
                                            borderRadius: '8px',
                                            boxShadow: '0px 4px 12px rgba(16, 24, 40, 0.08)',
                                            border: '1px solid #F2F4F7',
                                            minWidth: '150px',
                                        }
                                    }, children: [_jsx(MenuItem, { onClick: handleExportDataClick, className: "all-Font-Sizes-12", sx: { color: "#344054", fontWeight: 500, py: 1, px: 2, '&:hover': { backgroundColor: '#F9FAFB', color: '#ff008e' } }, children: "Export Data" }), _jsx(MenuItem, { onClick: handleExportArticlesClick, className: "all-Font-Sizes-12", sx: { color: "#344054", fontWeight: 500, py: 1, px: 2, '&:hover': { backgroundColor: '#F9FAFB', color: '#ff008e' } }, children: "Export Articles" })] }))] }) }), _jsxs(Box, { className: "table-container", sx: { ...tableContainerSx, position: "relative" }, children: [_jsx(CommonTable, { columns: getColumns(hasBaselineAccess), rows: clients, renderCell: renderCell, isFirstLoad: loading && !clients.length, sortConfig: { columnId: sortBy, direction: sortOrder }, onSortChange: (id, dir) => dispatch(setSortConfig({ sortBy: id, sortOrder: dir || "asc" })), expandTrigger: EXPAND_TRIGGERS.NONE, enableColumnActions: false, enableColumnReorder: false, enableColumnPinning: false, enableColumnFilters: false, enableColumnVisibility: false, enableSelection: false, enableSummaryRow: showSummaryRow, renderSummaryRow: showSummaryRow ? renderSummaryRow : undefined, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM, type: TABLE_TYPES.GROUPED, isLoading: false, stickyHeader: true, maxHeight: "calc(100vh - 190px)", tableHeadClassName: "reusable-table-header-label all-Font-Sizes" }), (loading || toggleLoading) && _jsx(Box, { sx: { position: "absolute", inset: 0, background: "white", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }, children: _jsx(CommonLoader, { className: "table-loader" }) }), pagination && pagination.total_clients > 0 && (_jsx(Box, { className: "user-list-pagination-wrapper", children: _jsx(Pagination, { currentPage: pagination.page, totalPages: pagination.total_pages, onPageChange: (p) => dispatch(setPage(p)), maxVisiblePages: 5, totalEntries: pagination.total_clients, itemsPerPage: pagination.limit, showControls: false }) }))] })] }), _jsx(ExportArticlesDialog, { open: exportArticlesModalOpen, onClose: () => setExportArticlesModalOpen(false), domainUrl: domainUrl, onDomainUrlChange: (val) => { setDomainUrl(val); setExportArticlesError(null); }, onSubmit: handleExportArticlesSubmit, loading: exportingArticles, error: exportArticlesError })] }));
}
